import React from "react";
import {connect} from "react-redux";
import {ACTION_STATES} from "core/actions";
import {makeStyles} from "@mui/styles";
import {statisticsStyles} from "components/statistics/styles";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ReferenceArea,
    ResponsiveContainer,
    Text,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import {assertArray} from "core/validation";
import {RATING_CATEGORIES, RATING_SUB_CATEGORIES_REPORT} from "core/dataConstants";
import printJS from "print-js";
import {IconButton} from "@mui/material";
import {Print} from "@mui/icons-material";
import {cloneDeep} from "lodash";
import {
    DATE_FORMAT_FULL_INPUT_ZONE,
    DATE_FORMAT_TEXT_INPUT,
    formatDate,
    reportIntervalToDate
} from "components/common/dateUtils";
import AppGlobalStore from "../../AppGlobalStore";
import {currentFullNameSelector} from "components/login/logic/actions";
import {subMonths} from "date-fns";

const useStyles = makeStyles(statisticsStyles);

const BAR_WIDTH = 60;


function  CustomizedAxisTick({ x, y, getTotalByCategory, payload } ) {
    const total = getTotalByCategory(payload.value);
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} textAnchor="end" fill="#666" fontWeight={600} >
                {payload.value} ({total})
            </text>
        </g>
    );
}

function CustomizedLabel({x, y, fill, value, index, nameId, getCategory}) {
    const catLabel = getCategory(index) + nameId.split("rating_")[1];
    return <Text
        x={x}
        y={y}
        dy={-4}
        dx={BAR_WIDTH / 2}
        fill={fill}
        textAnchor="middle">
        {`${catLabel}: ${value}`}
    </Text>;
}



function CustomizedTooltip({payload, label}) {
    let total = 0;
    payload.map((entry) => (
        total += entry.value
    ));
    return (
        <div className={"recharts-default-tooltip"} style={{background: "white", padding: "5px 10px"}}>
            <p style={{fontWeight: 600}}>Category {label}: <b>{total}</b></p>
            {payload.map((p) => {
                const labelS = `${label}-${p.name.split("Sub Category")[1]}`;
                return <p key={labelS} className="label" style={{color: p.fill}}>
                    {`${labelS} : ${p.value}`}
                </p>;
            })}
        </div>
    );
}

function FilmReleaseStatisticsChartView({data = [], ratingCategory, loadingStatus, companies, dateInterval}) {

    const classes = useStyles();
    const isLoading = loadingStatus === ACTION_STATES.SEND || loadingStatus === ACTION_STATES.NO_REQUEST;
    const failed = loadingStatus === ACTION_STATES.FAILED;
    const bars = [];

    function getCategory(index) {
        return RATING_CATEGORIES[index]?.label;
    }

    function convertSvgToImage(svg, width, height) {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const blobSvg = new Blob([svg], {type: "image/svg+xml"});
        const url = URL.createObjectURL(blobSvg);
        const img = new Image();
        const promise = new Promise((resolve) => {
            img.onload = () => {
                const context = canvas.getContext("2d");
                context.drawImage(img, 0, 0, width, height);
                const png = cloneDeep(canvas.toDataURL());
                canvas.remove();
                resolve(png);

            };
        });

        img.src = url;
        return promise;
    }

    const buildPDFPrintStyle = () => {
        return `
      @page { size: Letter landscape; }
      body { font-family: Arial; }
    `;
    };

    function printPdf() {
        const svg = document.querySelector("#filmReleaseStatisticsDiv svg.recharts-surface");
        svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");

        convertSvgToImage(svg.outerHTML, 5000, 1500).then(result => {
            printJS({
                printable: result,
                type: "image",
                base64: true,
                documentTitle:"Statistics Report",
                style: buildPDFPrintStyle(),
                ignoreElements: ["backButton", "currentMetricLabel", "monthLegendLabel", "totalLabel"],
                header: printHeader()
            });
        });
    }

    function printHeader() {
        const dateIn = reportIntervalToDate({
            ...dateInterval,
            interval: dateInterval.value.value
        });
        let dateFrom = formatDate(dateIn.from, DATE_FORMAT_TEXT_INPUT);
        let dateTo = formatDate(dateIn.to, DATE_FORMAT_TEXT_INPUT);
        const currentUserFullName = AppGlobalStore.reduceStore(currentFullNameSelector);
        const date = formatDate(new Date(), DATE_FORMAT_FULL_INPUT_ZONE, true);
        if(dateFrom && !dateTo) {
            dateTo = formatDate(new Date(), DATE_FORMAT_TEXT_INPUT);
        } else if(!dateFrom && dateTo) {
            const dateF = subMonths(new Date(), 1);
            dateFrom = formatDate(dateF, DATE_FORMAT_TEXT_INPUT);
        }

        const companiesSt = assertArray(companies) ? `<h6 style="font-size: 10px; font-weight: 400">Companies: <b>${companies.map(c => c.label).join(", ")}</b></h6> `: "" ;
        return `
              <h5 style="font-size: 12px; font-weight: 600; margin-top: 5px">Film Release Category Statistics</h5>
              <h6 style="font-size: 10px; font-weight: 400">Date range: <b>${dateFrom}</b> to <b>${dateTo}</b></h6>
              ${companiesSt}
              <h6 style="font-size: 10px; font-weight: 400">Created by: <b>${currentUserFullName}</b></h6>
              <h6 style="font-size: 10px; font-weight: 400">Created: <b>${date}</b></h6>
              `;
    }

    function printGraph() {
        printPdf();
    }

    function getTotalByCategory(category) {
        return data.find(e => e.ratingCategory === category).total;
    }


    RATING_SUB_CATEGORIES_REPORT.map((subCat) => {
        const dataKey = subCat.id;
        const e = <Bar
            dataKey={dataKey}
            barSize={BAR_WIDTH}
            name={subCat.label}
            fill={subCat.color}
            key={subCat.id}
            minPointSize={1}
            label={<CustomizedLabel getCategory={getCategory} nameId={subCat.id}/>}
            legendType={"square"}>
        </Bar>;
        bars.push(e);
    });
    function yAxisMaxFunc(dataMax) {
        if(dataMax < 10) {
            return dataMax + 5;
        }
        return dataMax + parseInt(dataMax * 0.3);
    }
    const chart = (
        <ResponsiveContainer width={"100%"} >
            <BarChart data={data} barCategoryGap={20}  >
                <CartesianGrid strokeDasharray="3 3"/>
                <XAxis dataKey="ratingCategory" tick={<CustomizedAxisTick getTotalByCategory={getTotalByCategory} />} />
                <YAxis allowDecimals={false} domain={[0, yAxisMaxFunc]}/>
                <Tooltip isAnimationActive={false} content={<CustomizedTooltip/>}/>
                <Legend verticalAlign={"bottom"} align={"center"}
                    wrapperStyle={{bottom: - 20}}
                    layout={"horizontal"}/>
                {!ratingCategory && (
                    <>
                        {RATING_CATEGORIES.map((cat,index) => {
                            const fill = index%2 === 0 ? "#e0e0e0" : "#eceff1";
                            return <ReferenceArea
                                key={`${cat}_${index}`}
                                x1={cat.value}
                                x2={cat.value}
                                y1={0}
                                fill={fill}/>;
                        })}
                    </>
                )}
                {bars}


            </BarChart>
        </ResponsiveContainer>
    );
    return (
        <div className={classes.responsiveChartContainer}>
            <div style={{textAlign: "right"}}>
                <IconButton aria-label="print" color={"secondary"} onClick={printGraph}>
                    <Print/>
                </IconButton>
            </div>

            <div className={classes.responsiveChart} id={"filmReleaseStatisticsDiv"} style={{width: "100%"}}>
                {!isLoading && <>
                    {chart}
                </>}

            </div>
            {isLoading && !failed && <div>Loading....</div>}
            {!isLoading && failed && <div>Failed</div>}
        </div>

    );
}


const FilmReleaseStatisticsChart = connect(({statistics}) => ({
    data: statistics.filmReportData,
    loadingStatus: statistics.filmQueryStatus,
    ratingCategory: statistics.filmReleaseForm.ratingCategory,
    dateInterval: statistics.filmReleaseForm.dateInterval,
    companies:statistics.filmReleaseForm.companies
}))(FilmReleaseStatisticsChartView);
export default FilmReleaseStatisticsChart;
